import { useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { fetchRechargeCalcPreview } from '../rechargeService';
import { AMOUNT_MIN, CALC_PREVIEW_DEBOUNCE_WAIT } from '../constants';
import type { RechargeCalcState } from '../types';

interface UseRechargeCalcParams {
  l2_demand_id: number;
  l3_demand_id?: number;
  recharge_order_id?: number;
}

/** debounce 等待时长（ms）：用户停止输入后触发实时核算 */
const CALC_PREVIEW_DEBOUNCE_WAIT_ = CALC_PREVIEW_DEBOUNCE_WAIT;

/**
 * 充值核算实时预判：用户填入金额并停止输入（debounce 500ms）后调 calc-preview，
 * 返回 is_over_charge + need_over_charge_reason；include_early_recharge=true 时额外返回
 * is_early_recharge + need_early_recharge_reason（提交报错重算时传 true 刷新提前充值标识）。
 * is_early_recharge 由 draft 返回一次初始值（与金额无关），include_early_recharge=true 时 calc-preview 刷新。
 * 前端据此控制超充/提前充值原因框显隐 + 必填（仅 PLANNER 发起）。
 *
 * 不用 onValuesChange 每次 onChange 触发：InputNumber 每次按键都触发会频繁请求，
 * 且输入过程中（未到最终金额）反复预判无意义；debounce 到"输入完成"再调。
 */
export function useRechargeCalc(params: UseRechargeCalcParams) {
  const [calc, setCalc] = useState<RechargeCalcState>({});

  const { run: previewAmount, cancel, flush } = useDebounceFn(
    (amount?: number, includeEarly: boolean = false) => {
      if (!amount || amount < AMOUNT_MIN) {
        // 金额无效（空/0/负/<最小值）：重置超充预判，隐藏超充原因框
        // 提前充值与金额无关，不重置（保留 draft/上次刷新值）
        setCalc((prev) => ({
          ...prev,
          is_over_charge: false,
          need_over_charge_reason: false,
        }));
        return;
      }
      fetchRechargeCalcPreview(
        {
          l2_demand_id: params.l2_demand_id,
          l3_demand_id: params.l3_demand_id,
          amount,
          recharge_order_id: params.recharge_order_id,
          include_early_recharge: includeEarly,
        },
        { alertError: false }
      )
        .then((res) => {
          if (res?.code === 1 && res.data) {
            const d = res.data;
            setCalc((prev) => ({
              ...prev,
              is_over_charge: d.is_over_charge,
              need_over_charge_reason: d.need_over_charge_reason,
              // 提前充值仅在 includeEarly=true 时后端返回（否则字段省略=undefined）；非空才更新，保留 prev
              ...(d.is_early_recharge != null
                ? { is_early_recharge: d.is_early_recharge }
                : {}),
              ...(d.need_early_recharge_reason != null
                ? { need_early_recharge_reason: d.need_early_recharge_reason }
                : {}),
            }));
          }
        })
        .catch(() => {});
    },
    { wait: CALC_PREVIEW_DEBOUNCE_WAIT_ }
  );

  /**
   * 提交报错重算（含提前充值）：立即触发 calc-preview（flush 跳过 debounce），
   * 刷新 is_over_charge/need_over_charge_reason + is_early_recharge/need_early_recharge_reason，
   * 让 TOCTOU 变化的行重新显示原因框。
   */
  const refreshWithEarly = (amount?: number) => {
    previewAmount(amount, true);
    flush();
  };

  return { calc, setCalc, previewAmount, refreshWithEarly, cancel, flush };
}
